// Vue
import _ from "lodash";
import { Component, Watch } from "vue-property-decorator";
// Template
import template from "./template.vue";
// Base Module
import ModuleBase from "#/common/ModuleBase";
// Components
import ModuleWrapper from "#/components/module_wrapper/ModuleWrapper";
import UnimogCheckbox from "#/components/unimog_checkbox/UnimogCheckbox";
import { Observer } from "mobx-vue";
import { Api } from "../../../api";
import { ProductCountResponseOptions } from "../../../common/interfaces/unimog/ProductCountResponse";
import { debounce } from "../../../common/Utils";
import { UnimogProduct } from "../../../common/interfaces/unimog/UnimogProduct";
import { UnimogPartner } from "../../../common/interfaces/unimog/UnimogPartner";
import { Filter } from "../../../common/interfaces/unimog/Filter";

@Observer
@Component({
  mixins: [template],
  components: {
    ModuleWrapper,
    UnimogCheckbox,
  },
})
export class PageFilterModule extends ModuleBase {
  public getBackgroundColorFromInstance(instance: object) {
    return "white";
  }

  private activeTab: "product" | "partner" = "product";

  private filter: Filter = {
    freitext: "",
    baureihen: [],
    branchen: [],
    aufbaupartner: "",
    fahrzeugtyp: "",
    baumuster: "",
    euronorm: "",
    radstand: "",
    land: "",
    einsatzgebiet: "",
    partnerstatus: "",
  };

  private freitextObj: object = {
    freitext: "",
  };

  private isLoading: boolean = false;

  private countData: ProductCountResponseOptions | null = null;

  private products: UnimogProduct[] = [];
  private currentPageProducts: number = 1;
  private hasMoreProducts: boolean = true;

  private partner: UnimogPartner[] = [];
  private currentPagePartner: number = 1;
  private hasMorePartner: boolean = true;

  private isFilterVisible = true;

  private isEconic: boolean = window.location.hostname.indexOf("econic") > 0;

  private initalLoadComplete: boolean = false;

  created() {
    let activeTab = window.localStorage.getItem("unimog-page-filter__tab");
    if (activeTab) {
      // @ts-ignore
      this.activeTab = activeTab;
    }

    let filter = window.localStorage.getItem("unimog-page-filter__filter");

    if (filter) {
      this.filter = JSON.parse(filter);
    }

    let isFilterVisible = window.localStorage.getItem(
      "unimog-page-filter__filter--visible"
    );
    if (isFilterVisible) {
      this.isFilterVisible = JSON.parse(isFilterVisible);
    }
  }

  mounted() {
    this.loadData();
    window.addEventListener("scroll", this.onScroll);
  }

  destroyed() {
    window.removeEventListener("scroll", this.onScroll);
  }

  /**
   * Wenn 'agrologistik', 'forstwirtschaft' und 'kommunalmbs' gewählt
   * worden sind muss auch 'lohnunternehmerwelt' ausgewählt sein.
   */
  @Watch("filter.branchen")
  private onBranchenChange() {
    let arr = ["agrologistik", "forstwirtschaft", "kommunalmbs"];

    let allChecked = arr.every((f) => {
      return this.filter.branchen.indexOf(f) >= 0;
    });

    if (
      allChecked &&
      this.filter.branchen.indexOf("lohnunternehmerwelt") == -1
    ) {
      this.filter.branchen.push("lohnunternehmerwelt");
    }
  }

  @Watch("filter", { deep: true })
  private onFilterChange(newFilter, oldFilter) {
    window.localStorage.setItem(
      "unimog-page-filter__filter",
      JSON.stringify(this.filter)
    );
    this.loadDataDebounced();
  }

  @Watch("activeTab")
  private onActiveTabChange() {
    window.localStorage.setItem("unimog-page-filter__tab", this.activeTab);
  }

  @Watch("isFilterVisible")
  private onIsFilterVisibleChange() {
    window.localStorage.setItem(
      "unimog-page-filter__filter--visible",
      JSON.stringify(this.isFilterVisible)
    );
  }

  private loadDataDebounced = debounce(this.loadData, 400);

  private get productColumns() {
    let result: { [key: string]: { name: string; sortable: boolean } } = {};
    return {
      image: {
        name: " ",
        sortable: false,
      },
      aufbauarten: {
        name: this.instance["translation"]["table_head_products_aufbauart"],
        sortable: false,
      },
      aufbaupartner: {
        name: this.instance["translation"][
          "table_head_products_aufbauhersteller"
        ],
        sortable: false,
      },
      produktname: {
        name: this.instance["translation"]["table_head_products_productname"],
        sortable: false,
      },
      fahrzeugtyp: {
        name: this.instance["translation"]["placeholder_fahrzeugtyp"],
        sortable: false,
      },
    };

    return result;
  }

  private get productTableData() {
    return this.products.map((p) => {
      return {
        image: p.vorschaubildUrl,
        aufbauarten: {
          aufbauarten: p.aufbauarten,
          isKombination: p.isKombination,
          isHighlight: p.isHighlight,
        },
        aufbaupartner: p.abhs,
        produktname: p.name,
        fahrzeugtyp: p.fahrzeugTyp,
        link: p.link,
      };
    });
  }

  private get partnerColumns() {
    let result: { [key: string]: { name: string; sortable: boolean } } = {};

    return {
      image: {
        name: " ",
        sortable: false,
      },
      aufbaupartner: {
        name: this.instance["translation"]["table_head_partner_aufbaupartner"],
        sortable: false,
      },
      branchen: {
        name: this.instance["translation"]["table_head_partner_branchen"],
        sortable: false,
      },
      adresse: {
        name: this.instance["translation"]["table_head_partner_adresse"],
        sortable: false,
      },
    };

    return result;
  }

  private get partnerTableData() {
    return this.partner.map((p) => {
      return {
        link: p.link,
        image: p.logo,
        aufbaupartner: {
          name: p.name,
          firmenzusatz: p.firmenzusatz,
          status: p.status,
        },
        branchen: p.branchen,
        adresse: {
          strasse: p.strasse,
          plzOrt: p.plzOrt,
        },
      };
    });
  }

  private get productPdfLink() {
    let paramsObj = {
      "f[q]": this.filter.freitext,
      "f[abh]": this.filter.aufbaupartner,
      "f[fahrzeugtyp]": this.filter.fahrzeugtyp,
      "f[baumuster]": this.filter.baumuster,
      "f[euronorm]": this.filter.euronorm,
      "f[radstand]": this.filter.radstand,
      "f[land]": this.filter.land,
      "f[einsatzgebiet]": this.filter.einsatzgebiet,
      "f[partnerlevel]": this.filter.partnerstatus,
    };

    this.filter.branchen.forEach((b, i) => {
      paramsObj["f[branche][" + i + "]"] = b;
    });

    this.filter.baureihen.forEach((b, i) => {
      paramsObj["f[baureihen][" + i + "]"] = b;
    });

    const params = new URLSearchParams(paramsObj);

    return (
      "/" +
      this.$store.ui.appCurrentLanguage +
      "/cms/unimog/productkatalog?" +
      params
    );
  }

  public async loadData() {
    this.isLoading = true;
    this.initalLoadComplete = false;
    this.hasMorePartner = true;
    this.hasMoreProducts = true;
    this.currentPagePartner = 1;
    this.currentPageProducts = 1;

    this.products = [];
    this.partner = [];

    let p1 = Api.unimog
      .getCount(
        this.filter.freitext,
        this.filter.baureihen,
        this.filter.branchen,
        this.filter.aufbaupartner,
        this.filter.fahrzeugtyp,
        this.filter.baumuster,
        this.filter.euronorm,
        this.filter.radstand,
        this.filter.land,
        this.filter.einsatzgebiet,
        this.filter.partnerstatus
      )
      .then((countData) => {
        this.countData = countData.options;
      });

    let p2 = await Api.unimog
      .getProductList(
        this.filter.freitext,
        this.filter.baureihen,
        this.filter.branchen,
        this.filter.aufbaupartner,
        this.filter.fahrzeugtyp,
        this.filter.baumuster,
        this.filter.euronorm,
        this.filter.radstand,
        this.currentPageProducts
      )
      .then((products) => {
        this.products = products;
      });

    let p3 = await Api.unimog
      .getPartnerList(
        this.filter.freitext,
        [], // this.filter.baureihen,
        [], // this.filter.branchen,
        this.filter.land,
        this.filter.einsatzgebiet,
        this.filter.partnerstatus,
        this.currentPagePartner
      )
      .then((partner) => {
        this.partner = partner;
      });

    await Promise.all([p1, p2, p3]);

    this.initalLoadComplete = true;
    this.isLoading = false;
    this.$nextTick(() => {
      this.onScroll();
    });
  }

  public async loadMoreProductData() {
    this.isLoading = true;
    this.currentPageProducts += 1;
    let products = await Api.unimog.getProductList(
      this.filter.freitext,
      this.filter.baureihen,
      this.filter.branchen,
      this.filter.aufbaupartner,
      this.filter.fahrzeugtyp,
      this.filter.baumuster,
      this.filter.euronorm,
      this.filter.radstand,
      this.currentPageProducts
    );

    if (products.length > 0) {
      this.products = this.products.concat(products);
    } else {
      this.hasMoreProducts = false;
    }

    this.isLoading = false;
    this.$nextTick(() => {
      this.onScroll();
    });
  }

  public async loadMorePartnerData() {
    this.isLoading = true;
    this.currentPagePartner += 1;
    let partner = await Api.unimog.getPartnerList(
      this.filter.freitext,
      [], //this.filter.baureihen,
      [], //this.filter.branchen,
      this.filter.land,
      this.filter.einsatzgebiet,
      this.filter.partnerstatus,
      this.currentPagePartner
    );

    if (partner.length > 0) {
      this.partner = this.partner.concat(partner);
    } else {
      this.hasMorePartner = false;
    }

    this.isLoading = false;
    this.$nextTick(() => {
      this.onScroll();
    });
  }

  private onInputBranchenCheckbox(trueValue, isChecked) {
    let arr = ["agrologistik", "forstwirtschaft", "kommunalmbs"];

    // Wenn 'Lohnunternehmerwelt' geklickt wurde ...
    if (trueValue === "lohnunternehmerwelt") {
      // ... und der Wert True ist
      if (isChecked) {
        // .. makieter alle anderen auch true
        arr.forEach((f) => {
          let index = this.filter.branchen.indexOf(f);
          if (index == -1) {
            this.filter.branchen.push(f);
          }
        });
      }
      // ... wen der Wert False ist und alle anderen auch makiert sind
      else if (arr.every((f) => this.filter.branchen.indexOf(f) >= 0)) {
        // ... entferne alle anderen
        arr.forEach((f) => {
          let index = this.filter.branchen.indexOf(f);
          if (index >= 0) {
            this.filter.branchen.splice(index, 1);
          }
        });
      }
    }
    // Wenn einer der anderen geklickt wurde ...
    else if (arr.indexOf(trueValue) >= 0) {
      // ... und er nicht makiert ist, aber Lohnunternehmerwelt makiert ist
      if (
        !isChecked &&
        this.filter.branchen.indexOf("lohnunternehmerwelt") >= 0
      ) {
        // ... entferne Lohnunternehmerwelt
        let index = this.filter.branchen.indexOf("lohnunternehmerwelt");
        if (index >= 0) {
          this.filter.branchen.splice(index, 1);
        }
      }
    }
  }

  private onShowFilter() {
    this.isFilterVisible = true;
  }

  private onHideFilter() {
    this.isFilterVisible = false;
  }

  private onResetFilter() {
    this.filter = {
      freitext: "",
      baureihen: [],
      branchen: [],
      aufbaupartner: "",
      fahrzeugtyp: "",
      baumuster: "",
      euronorm: "",
      radstand: "",
      land: "",
      einsatzgebiet: "",
      partnerstatus: "",
    };
  }

  private onScroll() {
    let targetElement = <HTMLElement>this.$refs["table"];
    let scrollTop = Math.max(
      window.pageYOffset,
      document.documentElement.scrollTop,
      document.body.scrollTop
    );
    let top = targetElement.getBoundingClientRect().top;
    let height = targetElement.getBoundingClientRect().height;
    let windowHeight =
      window.innerHeight ||
      window.document.documentElement.clientHeight ||
      window.document.getElementsByTagName("body")[0].clientHeight;

    if (
      !this.isLoading &&
      this.initalLoadComplete &&
      (scrollTop > top + height + (windowHeight * 2) / 3 ||
        top + height < windowHeight)
    ) {
      if (this.activeTab == "product" && this.hasMoreProducts) {
        this.loadMoreProductData();
      } else if (this.activeTab == "partner" && this.hasMorePartner) {
        this.loadMorePartnerData();
      }
    }
  }

  public onProductRowClicked(rowKey, event) {
    event.stopPropagation();
    let row = this.productTableData[rowKey];
    let isCtrlKey = event.ctrlKey;
    if (row) {
      let link = row.link;

      if (isCtrlKey) {
        window.open(link, "_blank");
      } else {
        window.location.href = link;
      }
    }
  }

  public onPartnerRowClicked(rowKey, event) {
    event.stopPropagation();
    let row = this.partnerTableData[rowKey];
    let isCtrlKey = event.ctrlKey;
    if (row) {
      let link = row.link;

      if (isCtrlKey) {
        window.open(link, "_blank");
      } else {
        window.location.href = link;
      }
    }
  }
}
